/**
 * useHotkey — a tiny keyboard-first helper. Binds a single key (e.g. "F8" to sign the selection)
 * at the document level, skipping repeats and firing only when not typing into a field (so F8 in a
 * comment box still types nothing destructive — though F-keys rarely matter, modal-open is the real
 * guard). Disabled when `enabled` is false (no selection / a modal is open).
 */
import { useEffect, useRef } from "react";

export function useHotkey(
  key: string,
  handler: () => void,
  enabled = true,
): void {
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.key !== key) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // Don't hijack the key while the user edits text (F-keys are safe, but stay conservative).
      const editing =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable === true;
      if (editing && key.length === 1) return;
      e.preventDefault();
      ref.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [key, enabled]);
}
